<template>
  <th class="binds-table-head" :id="id" :class="headClasses" :style="headStyles" @click="changeSort">
    <div class="binds-table-head-container" v-if="$slots.default">
      <div class="binds-table-head-label">
        <slot />
      </div>
    </div>

    <binds-ripple class="binds-table-head-container" :binds-disabled="!hasSort" v-else>
      <div class="binds-table-head-label">
        <binds-upward-icon class="binds-table-sortable-icon" v-if="hasSort">arrow_upward</binds-upward-icon>

        {{ label }}

        <binds-tooltip v-if="tooltip">{{ tooltip }}</binds-tooltip>
      </div>
    </binds-ripple>
  </th>
</template>

<script>
import BindsUpwardIcon from '../../core/icons/BindsUpwardIcon'
import BindsResizeObserver from '../../core/utils/BindsResizeObserver'

export default {
  name: 'BindsTableHead',
  components: {
    BindsUpwardIcon
  },
  props: {
    bindsNumeric: Boolean,
    numeric: Boolean,
    id: [String, Number],
    label: String,
    tooltip: String,
    sortBy: String
  },
  inject: ['BindsTable'],
  data: () => ({
    width: null,
    windowResizeObserver: null
  }),
  computed: {
    hasSort () {
      return this.BindsTable.sort && this.sortBy
    },
    isSorted () {
      if (this.BindsTable.sort) {
        return this.BindsTable.sort === this.sortBy
      }
    },
    isDescSorted () {
      return this.isSorted && this.BindsTable.sortOrder === 'desc'
    },
    isAscSorted () {
      return this.isSorted && this.BindsTable.sortOrder === 'asc'
    },
    headStyles () {
      return {
        width: this.width + 'px'
      }
    },
    headClasses () {
      return {
        'binds-numeric': this.numeric || this.bindsNumeric,
        'binds-sortable': this.hasSort,
        'binds-sorted': this.isSorted,
        'binds-sorted-desc': this.isDescSorted
      }
    }
  },
  methods: {
    changeSort () {
      if (this.hasSort) {
        if (this.isAscSorted) {
          this.BindsTable.sortOrder = 'desc'
        } else {
          this.BindsTable.sortOrder = 'asc'
        }

        this.BindsTable.sort = this.sortBy
        this.BindsTable.emitEvent('binds-sorted', this.BindsTable.sort)
        this.BindsTable.emitEvent('update:bindsSort', this.BindsTable.sort)
        this.BindsTable.emitEvent('update:bindsSortOrder', this.BindsTable.sortOrder)
        this.BindsTable.sortTable()
      }
    },
    getChildNodesBySelector (el, selector) {
      return Array.from(el.childNodes).filter(({ classList }) => classList && classList.contains(selector))
    },
    getNodeIndex (nodes, el) {
      return [].indexOf.call(nodes, el)
    },
    setWidth () {
      if (this.BindsTable.fixedHeader) {
        const cellSelector = 'binds-table-cell'
        const thEls = this.getChildNodesBySelector(this.$el.parentNode, 'binds-table-head')
        const tdEls = this.BindsTable.contentEl.querySelectorAll('tr:first-child .' + cellSelector)
        const nodeIndex = this.getNodeIndex(thEls, this.$el)

        this.width = tdEls[nodeIndex].offsetWidth
      }
    }
  },
  updated () {
    this.$nextTick().then(this.setWidth)
  },
  mounted () {
    this.$nextTick().then(this.setWidth)

    if (this.BindsTable.fixedHeader) {
      this.windowResizeObserver = new BindsResizeObserver(window, this.setWidth)
    }
  },
  beforeDestroy () {
    if (this.windowResizeObserver) {
      this.windowResizeObserver.destroy()
    }
  }
}
</script>

<style lang="scss">
  @import "../BindsAnimation/variables";

  .binds-table-head {
    padding: 0;
    position: relative;
    font-size: 12px;
    line-height: 16px;
    text-align: left;

    &:last-child:not(:first-child) .binds-table-head-label {
      padding-right: 24px;
    }

    &.binds-numeric {
      text-align: right;
    }

    &.binds-sortable:first-of-type,
    &.binds-table-cell-selection + .binds-sortable {
      .binds-table-sortable-icon {
        right: 8px;
        left: auto;
      }
    }

    .binds-icon {
      $size: 16px;
      width: $size;
      height: $size;
      font-size: $size;

      &:not(.binds-sortable-icon) {
        margin: 0 4px;
      }

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }
    }
  }

  .binds-sortable {
    cursor: pointer;

    &:hover,
    &.binds-sorted {
      .binds-table-sortable-icon {
        opacity: 1;
      }
    }

    &.binds-sorted-desc {
      .binds-table-sortable-icon {
        transform: translateY(-50%) rotate(180deg);
      }
    }
  }

  .binds-table-head-container {
    height: 56px;
    padding: 14px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .binds-table-head-label {
    height: 28px;
    padding-right: 32px;
    padding-left: 24px;
    display: inline-block;
    position: relative;
    overflow: hidden;
    line-height: 28px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .binds-table-sortable-icon {
    position: absolute;
    top: 50%;
    left: 0;
    transition: .3s $binds-transition-default-timing;
    transform: translateY(-50%);
    opacity: 0;
    color: rgba(#000, .38);
  }
</style>
